<i18n>
{
	"en": {
		"header": "Package Types",
		"noPackageTypesFound": "No Package Types...",
		"packageCode": "Code",
		"packageTypeCode": "Type",
		"packageName.no": "Package Name (NO)",
		"packageName.en": "Package Name (EN)",
		"length": "Length",
		"width": "Width",
		"height": "Height",
		"weight": "Weight",
		"chargableWeight": "Ch.Weight",
		"inactive": "Inactive",
		"isPallet": "Pallet",
		"palletPriceFactorPrice": "Pallet Price Factor Price",
		"palletPriceFactorTruck": "Pallet Price Factor Truck",
		"deletePackageType": "Delete Package Type",
		"newPackageType": "New PackageType",
		"failedToFetch": "Failed to fetch package types",
		"failedToDelete": "Failed to delete package type",
		"failedToUpdate": "Failed to update package type",
		"updatedPackageType": "Package Type Updated",
		"deletedPackageTypes": "Deleted package type | Deleted package type | Deleted {count} package types",
		"sync": "Copy from R10",
		"confirmSyncFromR10": "This will replace all existing package types. Are you sure you want to copy from R10?",
		"confirmLabel": "Copy",
		"syncedPackageTypes": "Successfully copied package types from R10",
		"failedToSync": "Failed to copy package types from R10"
	},
	"no": {
		"header": "Pakketype",
		"noPackageTypesFound": "Ingen pakketyper...",
		"packageCode": "Kode",
		"packageTypeCode": "Type",
		"packageName.no": "Beskrivelse (NO)",
		"packageName.en": "Beskrivelse (EN)",
		"length": "Lengde",
		"width": "Bredde",
		"height": "Høyde",
		"weight": "Vekt",
		"chargableWeight": "Fr.Weight",
		"inactive": "Inaktiv",
		"isPallet": "Palle",
		"palletPriceFactorPrice": "Palleprisfaktor pris",
		"palletPriceFactorTruck": "Palleprisfaktor bil",
		"deletePackageType": "Fjern pakketype",
		"newPackageType": "Legg til pakketype",
		"failedToFetch": "Kunne ikke hente pakketype",
		"failedToUpdate": "Kunne ikke oppdatere pakketype",
		"updatedPackageType": "Pakketype oppdatert",
		"deletedPackageTypes": "Pakketype ble slettet | Pakketype ble slettet | Slettet {count} pakketypes",
		"sync": "Kopier fra R10",
		"confirmSyncFromR10": "Dette vil erstatte alle eksisterende fraktbetingelser. Er du sikker på at du ønsker å kopiere fra R10?",
		"confirmLabel": "Kopier",
		"syncedPackageTypes": "Pakketyper ble kopiert fra R10",
		"failedToSync": "Kunne ikke kopiere pakketyper fra R10"
	}
}
</i18n>

<template>
	<t-vertical-panel class="packageTypes">
		<div slot="header" class="packageTypes-header">
			<t-panel-header size="md" icon="order">
				{{ $t('header') }}
			</t-panel-header>
		</div>
		<div slot="sub-header" class="packageTypes-sub-header">
			<t-button
				iconBefore="refresh"
				appearance="primary"
				width="150px"
				@click="onSyncPackageTypes"
			>{{$t('sync')}}</t-button>
			<t-button
				:disabled="rowsSelected == 0"
				iconBefore="trash"
				appearance="primary"
				intent="danger"
				width="150px"
				type="bp"
				@click="onDeletePackageTypes"
			>{{$tc('deletePackageType', rowsSelected)}}</t-button>
			<t-button
				appearance="primary"
				width="150px"
				@click="onAddPackageType"
			>{{$t('newPackageType')}}</t-button>
		</div>
		<div class="packageTypes-content">
			<t-grid
				v-if="packageTypes"
				ref="grid"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDefinitions"
				:rowHeight="40"
				:items="packageTypes"
				:selectableMode="['checkbox']"
				localStorageId="packageTypesList"
				selectable
				@selected="onRowsSelected"
			>
				<template #packageCode="{ item }">
					<t-text-input
						:value="item.packageCode"
						:isInvalid="errors.code && item.id === errors.id"
						@change="updateValue(item, 'packageCode', $event)"
					/>
				</template>
				<template #packageTypeCode="{ item }">
					<t-text-input
						:value="item.packageTypeCode"
						:isInvalid="errors.code && item.id === errors.id"
						@change="updateValue(item, 'packageTypeCode', $event)"
					/>
				</template>
				<template #packageName.no="{ item }">
					<t-text-input
						:value="item.packageName.no"
						:isInvalid="errors.packageName && item.id === errors.id"
						@change="updateValue(item, 'packageName.no', $event)"
					/>
				</template>
				<template #packageName.en="{ item }">
					<t-text-input
						:value="item.packageName.en"
						:isInvalid="errors.packageName && item.id === errors.id"
						@change="updateValue(item, 'packageName.en', $event)"
					/>
				</template>
				<template #inactive="{ item }">
					<div style="display: flex; flex-direction: row; ">
						<t-checkbox
							style="margin-right: 15px"
							:checked="item.inactive"
							name="inactive"
							@change="updateValue(item, 'inactive', $event)"
						/>
					</div>
				</template>
				<template #isPallet="{ item }">
					<div style="display: flex; flex-direction: row; ">
						<t-checkbox
							style="margin-right: 15px"
							:checked="item.isPallet"
							name="isPallet"
							@change="updateValue(item, 'isPallet', $event)"
						/>
					</div>
				</template>
				<template #length="{ item }">
					<t-number-input
						:value="item.length"
						:isInvalid="errors.code && item.id === errors.id"
						hideZeroValue
						@change="updateValue(item, 'length', $event)"
					/>
				</template>
				<template #width="{ item }">
					<t-number-input
						:value="item.width"
						:isInvalid="errors.code && item.id === errors.id"
						hideZeroValue
						@change="updateValue(item, 'width', $event)"
					/>
				</template>
				<template #height="{ item }">
					<t-number-input
						:value="item.height"
						:isInvalid="errors.code && item.id === errors.id"
						hideZeroValue
						@change="updateValue(item, 'height', $event)"
					/>
				</template>
				<template #weight="{ item }">
					<t-number-input
						:value="item.weight"
						:isInvalid="errors.code && item.id === errors.id"
						hideZeroValue
						@change="updateValue(item, 'weight', $event)"
					/>
				</template>
				<template #chargableWeight="{ item }">
					<t-number-input
						:value="item.chargableWeight"
						:isInvalid="errors.code && item.id === errors.id"
						hideZeroValue
						@change="updateValue(item, 'chargableWeight', $event)"
					/>
				</template>
				<template #palletPriceFactorPrice="{ item }">
					<t-number-input
						:value="item.palletPriceFactorPrice"
						:isInvalid="errors.code && item.id === errors.id"
						hideZeroValue
						@change="updateValue(item, 'palletPriceFactorPrice', $event)"
					/>
				</template>
				<template #palletPriceFactorTruck="{ item }">
					<t-number-input
						:value="item.palletPriceFactorTruck"
						:isInvalid="errors.code && item.id === errors.id"
						hideZeroValue
						@change="updateValue(item, 'palletPriceFactorTruck', $event)"
					/>
				</template>
			</t-grid>
			<t-no-content-panel
				v-else
				:text="$t('noPackageTypesFound')"
				icon="book"
				iconType="bp"
			/>
			<t-overlay
				:isShown="loading"
				absolute
				loader
			/>
		</div>
		<t-dialog
			:isShown="confirmSyncShown"
			:hasHeader="false"
			:confirmLabel="$t('confirmLabel')"
			intent="danger"
			icon="cog"
			iconType="bp"
			width="480px"
			@ok="onConfirmSync"
			@close="confirmSyncShown = false"
		>
			{{$tc('confirmSyncFromR10')}}
		</t-dialog>
	</t-vertical-panel>
</template>

<script>
import { mapState, mapActions } from './store/packageTypes.store.js';

export default {
	name: 'PackageTypes',
	data() {
		return {
			defaultColumnDefinitions: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			rowsSelected: 0,
			confirmSyncShown: false
		};
	},
	computed: {
		...mapState([
			'packageTypes',
			'loading',
			'errors'
		]),
		columnDefinitions() {
			return [
				{
					field: 'packageCode',
					displayName: this.$t('packageCode'),
					width: 90,
					align: 'center'
				},
				{
					field: 'packageTypeCode',
					displayName: this.$t('packageTypeCode'),
					width: 90,
					align: 'center'
				},
				{
					field: 'packageName.no',
					displayName: this.$t('packageName.no'),
					minWidth: 100
				},
				{
					field: 'packageName.en',
					displayName: this.$t('packageName.en'),
					minWidth: 100
				},
				{
					field: 'length',
					displayName: this.$t('length'),
					type: 'number',
					width: 120
				},
				{
					field: 'width',
					displayName: this.$t('width'),
					type: 'number',
					width: 120
				},
				{
					field: 'height',
					displayName: this.$t('height'),
					type: 'number',
					width: 120
				},
				{
					field: 'weight',
					displayName: this.$t('weight'),
					type: 'number',
					width: 120
				},
				{
					field: 'chargableWeight',
					displayName: this.$t('chargableWeight'),
					type: 'number',
					width: 120
				},
				{
					field: 'inactive',
					displayName: this.$t('inactive'),
					align: 'center',
					width: 60
				},
				{
					field: 'isPallet',
					displayName: this.$t('isPallet'),
					align: 'center',
					width: 60
				},
				{
					field: 'palletPriceFactorPrice',
					displayName: this.$t('palletPriceFactorPrice'),
					type: 'number',
					width: 120
				},
				{
					field: 'palletPriceFactorTruck',
					displayName: this.$t('palletPriceFactorTruck'),
					type: 'number',
					width: 120
				},
			];
		}
	},
	async mounted() {
		try {
			await this.getPackageTypes();
		} catch (e) {
			this.$notify({
				type: 'error',
				title: this.$t('failedToFetch')
			});
		}
	},
	methods:{
		...mapActions([
			'getPackageTypes',
			'setPackageType',
			'deletePackageTypes',
			'addPackageType',
			'syncPackageTypes'
		]),
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		async updateValue(packageType, prop, data ) {
			try {
				await this.setPackageType({ packageType: packageType, data, prop });
				this.$notify({
					type: 'success',
					title: this.$t('updatedPackageType'),
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdate'),
				});
			}
		},
		async onDeletePackageTypes() {
			let selectedPackageTypes = this.$refs.grid.getSelectedItems();
			if (!selectedPackageTypes) return;
			try {
				await this.deletePackageTypes(selectedPackageTypes);
				this.$notify({
					type: 'success',
					title: this.$tc('deletedPackageTypes', selectedPackageTypes.length)
				});
				this.rowsSelected = 0;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToDelete')
				});
			}
		},
		async onAddPackageType() {
			try {
				await this.addPackageType();
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToAdd')
				});
			}
		},
		onSyncPackageTypes() {
			this.confirmSyncShown = true;
		},
		async onConfirmSync() {
			try {
				await this.syncPackageTypes();
				this.$notify({
					type: 'success',
					title: this.$tc('syncedPackageTypes')
				});
				await this.getPackageTypes({ force: true });
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToSync')
				});
			}
		}
	}
};

</script>

<style lang="scss" scoped>
.packageTypes {
	.packageTypes-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.packageTypes-sub-header {
		display: flex;
		padding: 0px 15px;
		height: 100%;
		align-items: center;
		justify-content: flex-end;

		> button {
			margin: 10px;
		}
	}
	.packageTypes-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px;
	}
}
.list-item {
	display: flex;
	align-items: center;
	justify-items: flex-start;

	> div {
		margin-left: 10px;
	}
}
</style>
